import React from "react";
import { SectionHeading } from "components/features/section-heading";
import ApiModel from "components/api-documentation/api-model/api-model";
import ApiEnum from "components/api-documentation/api-enum/api-enum";
import ApiLinks from "components/api-documentation/api-links/api-links";

// Create Employee
import {
  attributes as employeeCreateAttributes,
  payload as employeeCreatePayload,
  responseAttributes as employeeCreateResponseAttributes,
  successResponse as employeeCreateSuccessResponse,
  failureResponse as employeeCreateFailureResponse,
} from "constants/api-documentation-data/webhooks-employee-create";
import { attributes as applicantAttributes } from "constants/api-documentation-data/webhooks-employee-create-applicant";

// Update Employee
import {
  attributes as employeeUpdateAttributes,
  payload as employeeUpdatePayload,
  responseAttributes as employeeUpdateResponseAttributes,
  successResponse as employeeUpdateSuccessResponse,
  failureResponse as employeeUpdateFailureResponse,
} from "constants/api-documentation-data/webhooks-employee-update";
import { attributes as employeeAttributes } from "constants/api-documentation-data/webhooks-employee-update-employee";

// Create Employee Skill
import {
  attributes as employeeSkillCreateAttributes,
  payload as employeeSkillCreatePayload,
  responseAttributes as employeeSkillCreateResponseAttributes,
  successResponse as employeeSkillCreateSuccessResponse,
  failureResponse as employeeSkillCreateFailureResponse,
} from "constants/api-documentation-data/webhooks-employee-skill-create";
import { attributes as employeeSkillCreateDataAttributes } from "constants/api-documentation-data/webhooks-employee-skill-create-data";

// Delete Employee Skill
import {
  attributes as employeeSkillDeleteAttributes,
  payload as employeeSkillDeletePayload,
  responseAttributes as employeeSkillDeleteResponseAttributes,
  successResponse as employeeSkillDeleteSuccessResponse,
  failureResponse as employeeSkillDeleteFailureResponse,
} from "constants/api-documentation-data/webhooks-employee-skill-delete";
import { attributes as employeeSkillDeleteDataAttributes } from "constants/api-documentation-data/webhooks-employee-skill-delete-data";

// Enums & Common Classes
import { attributes as applicantUploadAttributes } from "constants/api-documentation-data/webhooks-employee-skill-upload";

import {
  enumGender,
  enumPronouns,
  enumSocialInclusion,
  enumDriversLicences,
} from "constants/api-documentation-data/webhooks-common-enums";

// Create Voc
import {
  attributes as employeeVocCreateAttributes,
  payload as employeeVocCreatePayload,
  responseAttributes as employeeVocCreateResponseAttributes,
  successResponse as employeeVocCreateSuccessResponse,
  failureResponse as employeeVocCreateFailureResponse,
} from "constants/api-documentation-data/webhooks-employee-voc-create";
import { attributes as employeeVocCreateDataAttributes } from "constants/api-documentation-data/webhooks-employee-voc-create-data";
import { attributes as employeeVocCreateDataUploadAttributes } from "constants/api-documentation-data/webhooks-employee-voc-create-data-upload";

// Lookups
import {
  attributes as lookupBranchesAttributes,
  payload as lookupBranchesPayload,
} from "constants/api-documentation-data/webhooks-lookup-branches-depots-locations";

import {
  attributes as lookupSkillsAttributes,
  payload as lookupSkillsPayload,
} from "constants/api-documentation-data/webhooks-lookup-licences-skills-qualifications";

import {
  attributes as lookupEmployeeClassificationsAttributes,
  payload as lookupEmployeeClassificationsPayload,
} from "constants/api-documentation-data/webhooks-lookup-employee-classifications-types";

import {
  attributes as lookupEmployeesAttributes,
  payload as lookupEmployeesPayload,
} from "constants/api-documentation-data/webhooks-lookup-employees";

export const apiDocsPublicPages = [
  {
    pageName: "api-documentation/webhooks",
    title: "Staffd Webhooks Documentation | Staffd",
    description: "",
    content: (
      <>
        <SectionHeading
          heading="Staffd Webhooks Documentation"
          subHeading="(Last Updated: 27.09.2022)"
        >
          <div className="mt-4" style={{ width: "100%" }}>
            <h3>Webhooks</h3>

            <ApiLinks
              links={[
                {
                  tag: "#create-employee",
                  text: "Create Employee - HTTP POST triggered when an admin user syncs a completed applicant via the Sync Applicant Details page.",
                },
                {
                  tag: "#update-employee",
                  text: "Update Employee - HTTP POST triggered when an admin user syncs an employee re-induction via the Sync Employee Details page, or when an admin changes or approves an employee detail change via the Manage Employee Details page.",
                },
              ]}
            />
            <ApiLinks
              links={[
                {
                  tag: "#create-employee-skill",
                  text: "Create Employee Skill - HTTP POST triggered when an admin changes or approves an employee skill change via the Manage Employee Details page.",
                },
                {
                  tag: "#delete-employee-skill",
                  text: "Delete Employee Skill - HTTP POST triggered when an admin deletes or approves an employee skill deletion via the Manage Employee Details page.",
                },
              ]}
            />
            <ApiLinks
              links={[
                {
                  tag: "#create-employee-voc",
                  text: "Create Employee Verification of Competency (VOC) - HTTP POST triggered when an employee completes an eLearning module.",
                },
              ]}
            />

            <h3>Webhook Signatures</h3>

            <ApiLinks
              links={[
                {
                  tag: "#webhook-signatures",
                  text: "Webhook Signature - How to Calculate Secure Salted Hash.",
                },
              ]}
            />

            <h3>External System Lookups</h3>

            <ApiLinks
              links={[
                {
                  tag: "#branches-depots-locations-lookup",
                  text: "Branches / Depots / Locations Lookup.",
                },
                {
                  tag: "#licences-skills-qualifications-lookup",
                  text: "Licences, Skills & Qualifications Lookup.",
                },
                {
                  tag: "#employee-classifications-types-lookup",
                  text: "Employee Classifications / Types Lookup.",
                },
                {
                  tag: "#employees-lookup",
                  text: "Employees Lookup.",
                },
              ]}
            />

            <section className="api-section">
              <h3 id="create-employee">Create Employee</h3>
              <p>
                HTTP POST triggered when an admin user syncs a completed
                applicant via the Sync Applicant Details page.
              </p>
              <ApiLinks
                links={[
                  {
                    tag: "#create-employee-object",
                    text: "CreateEmployee Object",
                  },
                  { tag: "#applicant-object", text: "Applicant object" },
                  {
                    tag: "#skill-upload-object",
                    text: "SkillUpload object",
                  },
                  {
                    tag: "#gender-type",
                    text: "GenderType enum",
                  },
                  {
                    tag: "#pronouns-type",
                    text: "PronounsType enum",
                  },
                  {
                    tag: "#social-inclusion-type",
                    text: "SocialInclusionType enum",
                  },
                  {
                    tag: "#drivers-licence-type",
                    text: "DriversLicenceType enum",
                  },
                ]}
              />

              <h4 className="mt-3" id="create-employee-object">
                CreateEmployee object
              </h4>
              <ApiModel
                attributes={employeeCreateAttributes}
                payloadObj={employeeCreatePayload}
                responseAttributes={employeeCreateResponseAttributes}
                successResponseObj={employeeCreateSuccessResponse}
                failureResponseObj={employeeCreateFailureResponse}
                responseHeight="70px"
              />

              <h4 className="mt-3" id="applicant-object">
                Applicant object
              </h4>
              <ApiModel attributes={applicantAttributes} />
            </section>

            <section className="api-section">
              <h3 id="update-employee">Update Employee</h3>
              <p>
                HTTP POST triggered when an admin user syncs an employee
                re-induction via the Sync Employee Details page, or when an
                admin changes or approves an employee detail change via the
                Manage Employee Details page.
              </p>
              <ApiLinks
                links={[
                  {
                    tag: "#update-employee-object",
                    text: "UpdateEmployee Object",
                  },
                  { tag: "#employee-object", text: "Employee object" },
                  {
                    tag: "#skill-upload-object",
                    text: "SkillUpload object",
                  },
                  {
                    tag: "#gender-type",
                    text: "GenderType enum",
                  },
                  {
                    tag: "#pronouns-type",
                    text: "PronounsType enum",
                  },
                  {
                    tag: "#social-inclusion-type",
                    text: "SocialInclusionType enum",
                  },
                  {
                    tag: "#drivers-licence-type",
                    text: "DriversLicenceType enum",
                  },
                ]}
              />

              <h4 className="mt-3" id="update-employee-object">
                UpdateEmployee object
              </h4>
              <ApiModel
                attributes={employeeUpdateAttributes}
                payloadObj={employeeUpdatePayload}
                responseAttributes={employeeUpdateResponseAttributes}
                successResponseObj={employeeUpdateSuccessResponse}
                failureResponseObj={employeeUpdateFailureResponse}
                responseHeight="70px"
              />

              <h4 className="mt-3" id="employee-object">
                Employee object
              </h4>
              <ApiModel attributes={employeeAttributes} />
            </section>

            <section className="api-section">
              <h4 className="mt-3" id="skill-upload-object">
                SkillUpload object
              </h4>
              <ApiModel attributes={applicantUploadAttributes} />

              <h4 className="mt-3" id="gender-type">
                GenderType enum
              </h4>
              <ApiEnum enums={enumGender} />

              <h4 className="mt-3" id="pronouns-type">
                PronounsType enum
              </h4>
              <ApiEnum enums={enumPronouns} />

              <h4 className="mt-3" id="social-inclusion-type">
                SocialInclusionType enum
              </h4>
              <ApiEnum enums={enumSocialInclusion} />

              <h4 className="mt-3" id="drivers-licence-type">
                DriversLicenceType enum
              </h4>
              <ApiEnum enums={enumDriversLicences} />
            </section>

            <section className="api-section">
              <h3 id="create-employee-skill">Create Employee Skill</h3>
              <p>
                HTTP POST triggered when an admin changes or approves an
                employee skill change via the Manage Employee Details page.
              </p>
              <ApiLinks
                links={[
                  {
                    tag: "#create-employee-skill-object",
                    text: "CreateEmployeeSkill Object",
                  },
                  {
                    tag: "#create-employee-skill-data-object",
                    text: "CreateEmployeeSkillData object",
                  },
                  {
                    tag: "#skill-upload-object",
                    text: "SkillUpload object",
                  },
                ]}
              />

              <h4 className="mt-3" id="create-employee-skill-object">
                CreateEmployeeSkill object
              </h4>
              <ApiModel
                attributes={employeeSkillCreateAttributes}
                payloadObj={employeeSkillCreatePayload}
                responseAttributes={employeeSkillCreateResponseAttributes}
                successResponseObj={employeeSkillCreateSuccessResponse}
                failureResponseObj={employeeSkillCreateFailureResponse}
                payloadHeight="260px"
                responseHeight="70px"
              />

              <h4 className="mt-3" id="create-employee-skill-data-object">
                CreateEmployeeSkillData object
              </h4>
              <ApiModel attributes={employeeSkillCreateDataAttributes} />
            </section>

            <section className="api-section">
              <h3 id="delete-employee-skill">Delete Employee Skill</h3>
              <p>
                HTTP POST triggered when an admin deletes or approves an
                employee skill deletion via the Manage Employee Details page.
              </p>
              <ApiLinks
                links={[
                  {
                    tag: "#delete-employee-skill-object",
                    text: "DeleteEmployeeSkill Object",
                  },
                  {
                    tag: "#delete-employee-skill-data-object",
                    text: "DeleteEmployeeSkillData object",
                  },
                ]}
              />

              <h4 className="mt-3" id="delete-employee-skill-object">
                DeleteEmployeeSkill object
              </h4>
              <ApiModel
                attributes={employeeSkillDeleteAttributes}
                payloadObj={employeeSkillDeletePayload}
                responseAttributes={employeeSkillDeleteResponseAttributes}
                successResponseObj={employeeSkillDeleteSuccessResponse}
                failureResponseObj={employeeSkillDeleteFailureResponse}
                payloadHeight="260px"
                responseHeight="70px"
              />

              <h4 className="mt-3" id="delete-employee-skill-data-object">
                DeleteEmployeeSkillData object
              </h4>
              <ApiModel attributes={employeeSkillDeleteDataAttributes} />
            </section>

            <section className="api-section">
              <h3 id="create-employee-voc">
                Create Employee Verification of Competency (VOC)
              </h3>
              <p>
                HTTP POST triggered when an employee completes an eLearning
                module.
              </p>
              <ApiLinks
                links={[
                  {
                    tag: "#create-employee-voc-object",
                    text: "CreateEmployeeVoc Object",
                  },
                  {
                    tag: "#employee-voc-data-object",
                    text: "EmployeeVocData object",
                  },
                  {
                    tag: "#employee-voc-data-upload-object",
                    text: "EmployeeVocUpload object",
                  },
                ]}
              />

              <h4 className="mt-3" id="create-employee-voc-object">
                CreateEmployeeVoc object
              </h4>
              <ApiModel
                attributes={employeeVocCreateAttributes}
                payloadObj={employeeVocCreatePayload}
                responseAttributes={employeeVocCreateResponseAttributes}
                successResponseObj={employeeVocCreateSuccessResponse}
                failureResponseObj={employeeVocCreateFailureResponse}
                payloadHeight="260px"
                responseHeight="70px"
              />

              <h4 className="mt-3" id="employee-voc-data-object">
                EmployeeVocData object
              </h4>
              <ApiModel attributes={employeeVocCreateDataAttributes} />

              <h4 className="mt-3" id="employee-voc-data-upload-object">
                EmployeeVocUpload object
              </h4>
              <ApiModel attributes={employeeVocCreateDataUploadAttributes} />
            </section>

            <section className="api-section">
              <h3 id="webhook-signatures">
                Webhook Signature - How to Calculate Secure Salted Hash
              </h3>
              <p>
                All webhook requests will be signed with an MD5 hash calculated
                based on your client secret key and the payload data.
              </p>
              <p>
                Only you and the Staffd platform know your client secret key, so
                by calculating and comparing your own hash, you can confirm the
                webhook request has truly originated from the Staffd platform.
              </p>
              <p>
                To calculate your own hash, you will need to build a string
                comprising of your client secret key and the payload object
                expressed as a JSON string. When converting the payload object
                to a JSON string, ensure you are not using any spaces or
                indentation, and you are using camelCase property names and
                double quotes instead of single quotes.
              </p>
              <p>
                <strong>For example, </strong> if your client secret key is
                7606d5659e07448cb9a72e11794027aedb825f6b2bb84f2491250a3482e1f870
                and we use the example payload from CreateEmployee object, your
                value to hash should look like:
              </p>
              <pre
                dangerouslySetInnerHTML={{
                  __html: `7606d5659e07448cb9a72e11794027aedb825f6b2bb84f2491250a3482e1f870{"id":"0163f24e-d978-4016-caa6-08da33010a3b","firstName":"Sally","lastName":"Templeton","email":"sally@staffd.me","mobile":"0499857574","dateOfBirth":"1988-10-13T00:00:00","gender":1,"pronouns":3,"socialInclusions":[8,9],"positionApplied":"Casual - Traffic Controller","depot":"Glendenning","homeUnitNumber":"","homeStreetNumber":"231","homeStreetName":"Elizabeth St","homeCity":"Sydney","homeState":"NSW","homePostCode":"2000","homeFullAddress":"231 Elizabeth St, Sydney NSW 2000","postalUnitNumber":null,"postalStreetNumber":null,"postalStreetName":null,"postalCity":null,"postalState":null,"postalPostCode":null,"postalFullAddress":null,"emergencyContactFirstName":"Tom","emergencyContactLastName":"Templeton","emergencyContactRelationship":"Husband","emergencyContactPhone1":"0400 099 000","emergencyContactPhone2":"","emergencyAltContactFirstName":null,"emergencyAltContactLastName":null,"emergencyAltContactRelationship":null,"emergencyAltContactPhone1":null,"emergencyAltContactPhone2":null,"photoUrl":"https://staffd.blob.core.windows.net/demo-uploads/0163f24e-d978-4016-caa6-08da33010a3b-3a0fc74f-1f13-4723-bbc9-f82611d8d677.jpg","uploads":[{"uploadName":"TC Ticket","uploadUrls":["https://staffd.blob.core.windows.net/demo-uploads/0163f24e-d978-4016-caa6-08da33010a3b-7f6209ed-e0e1-4c77-9789-8c5a0e6629cb.jpg","https://staffd.blob.core.windows.net/demo-uploads/0163f24e-d978-4016-caa6-08da33010a3b-23f743b5-fc6d-4a1d-8e91-5ca96aaf1f0e.jpg"],"uploadNumber":"54363456","uploadExpiry":"2025-05-28T00:00:00","uploadIssue":null,"isDriversLicence":false,"driversLicenceNumber":null,"driversLicenceType":null,"driversLicenceExpiry":null,"driversLicenceAutoOnly":null},{"uploadName":"White Card","uploadUrls":["https://staffd.blob.core.windows.net/demo-uploads/0163f24e-d978-4016-caa6-08da33010a3b-af78ee00-8e5c-4b2b-b55f-f05f3739e6b5.jpg","https://staffd.blob.core.windows.net/demo-uploads/0163f24e-d978-4016-caa6-08da33010a3b-a894145d-53bd-4ec0-a557-04c03df594d9.jpg"],"uploadNumber":null,"uploadExpiry":null,"uploadIssue":null,"isDriversLicence":false,"driversLicenceNumber":null,"driversLicenceType":null,"driversLicenceExpiry":null,"driversLicenceAutoOnly":null},{"uploadName":"Drivers Licence","uploadUrls":["https://staffd.blob.core.windows.net/demo-uploads/0163f24e-d978-4016-caa6-08da33010a3b-e04d9033-ec6b-43ea-8baa-3162b6cbe2ca.jpg","https://staffd.blob.core.windows.net/demo-uploads/0163f24e-d978-4016-caa6-08da33010a3b-e08e6cc2-de38-4a06-899f-b0569051bc0b.jpg"],"uploadNumber":null,"uploadExpiry":null,"uploadIssue":null,"isDriversLicence":true,"driversLicenceNumber":"4356345","driversLicenceType":3,"driversLicenceExpiry":"2025-05-28T00:00:00","driversLicenceAutoOnly":false}]}`,
                }}
              ></pre>
              <p>
                Once run through your favourite MD5 hash method, the output
                should be:
              </p>
              <pre>6097942c30f77d62093baab06143f325</pre>
            </section>

            <section className="api-section">
              <h3 id="branches-depots-locations-lookup">
                Branches / Depots / Locations Lookup
              </h3>
              <p>
                The Branches / Depots / Locations Lookup can be optionally
                provided, however this is not a requirement to use any of the
                Staffd webhooks.
              </p>
              <p>
                You can provide a list of your Branches / Depots / Locations to
                be populated on the Sync Applicant Details page so the admin can
                select the specific Branch / Depot / Location that the new
                employee should be added to.
              </p>
              <ApiModel
                responseAttributes={lookupBranchesAttributes}
                successResponseObj={lookupBranchesPayload}
                responseHeight="240px"
              />
            </section>

            <section className="api-section">
              <h3 id="licences-skills-qualifications-lookup">
                Licences, Skills &amp; Qualifications Lookup
              </h3>
              <p>
                The Licences, Skills &amp; Qualifications Lookup can be
                optionally provided, however this is a{" "}
                <strong>
                  requirement to use the Create Employee Skill, Delete Employee
                  Skill or Create Employee Verification of Competency (VOC)
                </strong>{" "}
                webhooks.
              </p>
              <p>
                You can provide a list of your Licences, Skills &amp;
                Qualifications to be populated on the Update Module page so the
                admin can select the specific Licence / Skill / Qualification
                that the new employee&rsquo;s skill should be linked to.
              </p>
              <ApiModel
                responseAttributes={lookupSkillsAttributes}
                successResponseObj={lookupSkillsPayload}
              />
            </section>

            <section className="api-section">
              <h3 id="employee-classifications-types-lookup">
                Employee Classifications / Types Lookup
              </h3>
              <p>
                The Employee Classifications / Types Lookup can be optionally
                provided, however this is not a requirement to use any of the
                Staffd webhooks.
              </p>
              <p>
                You can provide a list of your Employee Classifications / Types
                to be populated on the Sync Applicant Details page so the admin
                can select the specific Employee Classification / Type that the
                new employee should be added to.
              </p>
              <ApiModel
                responseAttributes={lookupEmployeeClassificationsAttributes}
                successResponseObj={lookupEmployeeClassificationsPayload}
                responseHeight="240px"
              />
            </section>
            <section className="api-section">
              <h3 id="employees-lookup">Employees Lookup</h3>
              <p>
                The Employees Lookup can be optionally provided, however this is
                not a requirement to use any of the Staffd webhooks.
              </p>
              <p>If provided, Staffd will use this list to: </p>
              <ul>
                <li>
                  automatically attach the forgein key/external system ID number
                  of the Employee
                </li>
                <li>
                  automatically archive the accounts of employees recently made
                  inactive
                </li>
                <li>
                  automatically reactivate the accounts of employees recently
                  made active again
                </li>
              </ul>
              <ApiModel
                responseAttributes={lookupEmployeesAttributes}
                successResponseObj={lookupEmployeesPayload}
              />
            </section>
          </div>
        </SectionHeading>
      </>
    ),
  },
];
